import {
  BOOKING_STATUS_ACTION_DROPOFF,
  BOOKING_STATUS_ACTION_PICKUP,
  PASSENGER_STATUS_CANCELED,
  PASSENGER_STATUS_DROPOFF,
  PASSENGER_STATUS_ONBOARD,
  PLACE_STATUS_IN_PLACE,
  PLACE_STATUS_NOT_VISITED,
  PLACE_STATUS_VISITED,
  PLACE_STATUS_WAITING_FOR_CANCELLED_ACKNOWLEDGEMENT,
  PLACE_STATUS_WAITING_FOR_CONFIRM,
} from '../constants.ts';
import { Booking } from '../schema/Booking.ts';
import { Place } from '../schema/Place.ts';

export function getCurrentPlace(roadmap: Place[]): Place {
  let previousIndex = 0;
  for (let i = 0; i < roadmap.length; i++) {
    if (roadmap[i].status === PLACE_STATUS_IN_PLACE) {
      return roadmap[i];
    } else if (roadmap[i].status === PLACE_STATUS_VISITED) {
      previousIndex = i;
    }
  }
  return roadmap[previousIndex];
}

export function getNextPlace(roadmap: Place[]): Place {
  const statuses = new Set([
    PLACE_STATUS_NOT_VISITED,
    PLACE_STATUS_WAITING_FOR_CONFIRM,
    PLACE_STATUS_WAITING_FOR_CANCELLED_ACKNOWLEDGEMENT,
  ]);
  const nextPlaceIndex = roadmap.findIndex(place => statuses.has(place.status));
  return roadmap[Math.max(nextPlaceIndex, 0)];
}

export function displayInPlaceButton(
  currentPlace: Place,
  nextPlace: Place,
  selectedPlace: Place,
): boolean {
  let displayInPlaceButton = false;
  if (
    (nextPlace.order === selectedPlace.order &&
      [
        PLACE_STATUS_NOT_VISITED,
        PLACE_STATUS_WAITING_FOR_CONFIRM,
        PLACE_STATUS_WAITING_FOR_CANCELLED_ACKNOWLEDGEMENT,
      ].includes(selectedPlace.status) &&
      currentPlace.status === PLACE_STATUS_VISITED) ||
    (selectedPlace.status === PLACE_STATUS_NOT_VISITED && selectedPlace.order === 0)
  ) {
    displayInPlaceButton = true;
  }
  return displayInPlaceButton;
}

export function displayReadyToLeaveButton(currentPlace: Place, selectedPlace: Place): boolean {
  let displayReadyToLeaveButton = false;
  if (currentPlace.order === selectedPlace.order && selectedPlace.status === PLACE_STATUS_IN_PLACE) {
    displayReadyToLeaveButton = true;
  }
  return displayReadyToLeaveButton;
}

export function refreshSelectedPlace(selectedPlace: Place | null, roadmap: Place[]): Place | null {
  if (roadmap !== undefined && selectedPlace !== undefined && selectedPlace?.id !== undefined) {
    return roadmap.find((place: Place) => place.id === selectedPlace.id) || null;
  } else {
    return selectedPlace;
  }
}

export function getBookingsToPickUp(selectedPlace: Place | null): Booking[] | undefined {
  return selectedPlace?.bookings.filter(
    (booking: Booking) => booking.action === BOOKING_STATUS_ACTION_PICKUP,
  );
}

export function isPickUpButtonChecked(booking: Booking): boolean {
  return [PASSENGER_STATUS_ONBOARD, PASSENGER_STATUS_DROPOFF].includes(booking.passenger_status);
}

export function enablePickUpButton(
  selectedPlace: Place | null,
  currentPlace: Place | undefined,
  booking: Booking,
): boolean {
  return (
    selectedPlace?.order === currentPlace?.order &&
    ![PASSENGER_STATUS_ONBOARD, PASSENGER_STATUS_CANCELED].includes(booking.passenger_status)
  );
}

export function isDropOffButtonChecked(booking: Booking): boolean {
  return booking.passenger_status === PASSENGER_STATUS_DROPOFF;
}

export function enableDropOffButton(
  selectedPlace: Place | null,
  currentPlace: Place | undefined,
  booking: Booking,
): boolean {
  return (
    selectedPlace?.order === currentPlace?.order &&
    [PASSENGER_STATUS_ONBOARD].includes(booking.passenger_status)
  );
}

export function getBookingsToDropOff(selectedPlace: Place | null): Booking[] | undefined {
  return selectedPlace?.bookings.filter(
    (booking: Booking) => booking.action === BOOKING_STATUS_ACTION_DROPOFF,
  );
}

export function getConnectorUi(i: number, currentPlace: Place, nextPlace: Place) {
  const design: Record<string, string> = {
    top: 'calc(24px) !important',
  };
  if (i === currentPlace?.order) {
    if (currentPlace.status === PLACE_STATUS_VISITED && nextPlace.status === PLACE_STATUS_NOT_VISITED) {
      design['background'] = 'linear-gradient(90deg, #F2F2F2 30%, rgba(0,120,255,0.5) 50%, #F2F2F2 70%)';
      design['backgroundSize'] = '200% 100%';
      design['animation'] = 'glowSweep 4s ease-in-out infinite';
    }
  }
  return design;
}

export function getStepIndicatorUi(i: number, currentPlace: Place, nextPlace: Place) {
  let design;
  if (i < currentPlace.order) {
    // visited place
    design = {
      bgcolor: 'white',
      border: '4px solid',
      borderColor: '#4385E8',
    };
  } else if (i === currentPlace.order) {
    // current place
    if (currentPlace.status === PLACE_STATUS_VISITED) {
      // current place visited
      design = {
        bgcolor: 'white',
        border: '4px solid',
        borderColor: '#4385E8',
      };
    } else {
      // current place in place
      design = {
        '@keyframes pulse': {
          '50%': {
            opacity: 0.25,
          },
        },
        animation: 'pulse 1.5s linear infinite',
        bgcolor: 'white',
        border: '4px solid',
        borderColor: '#4385E8',
      };
    }
  } else if (i === nextPlace.order) {
    // next place
    if (currentPlace.status === PLACE_STATUS_VISITED) {
      design = {
        '@keyframes pulse': {
          '50%': {
            opacity: 0.25,
          },
        },
        animation: 'pulse 1.5s linear infinite',
        bgcolor: 'white',
        border: '4px solid',
        borderColor: '#A45CFF',
      };
    } else {
      design = {
        bgcolor: 'white',
        border: '4px solid',
        borderColor: '#A45CFF',
      };
    }
  } else {
    design = {
      bgcolor: 'white',
      border: '4px solid',
      borderColor: '#A45CFF',
    };
  }
  return design;
}

export function getCustomerIdentifier(booking: Booking): string {
  let customerIdentifier = booking.id.toString();
  if (booking.customer_first_name && booking.customer_last_name) {
    customerIdentifier = `${booking.customer_first_name} ${booking.customer_last_name}`;
  }
  return customerIdentifier;
}

export function shouldCustomerIdentifierBeCrossedOut(booking: Booking): boolean {
  return booking.passenger_status === PASSENGER_STATUS_CANCELED;
}

export function isLateToPlace(place: Place): boolean {
  let isLate = false;
  if (place?.max_time && place.status === PLACE_STATUS_NOT_VISITED) {
    const maxTime = new Date(place.max_time);
    const now = new Date();
    if (now > maxTime) {
      isLate = true;
    }
  }
  return isLate;
}

export function getRoadmapTextClass(place: Place): string {
  let initialClass = 'roadmap-departure-time text-basic';
  if (isLateToPlace(place)) {
    initialClass += ' ' + 'text-alert';
  }
  return initialClass;
}
